import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { SERVER_PROVIDER_ID, providerPurpose } from '../../core/providers';
import { listConversations } from '../../core/repositories/conversationRepo';
import { listProviders } from '../../core/repositories/providerRepo';
import { parseRoleModelConfig, resolveRoleConversation, validateRoleModelLimits } from '../../core/roleModel';
import { getDefaultModelSettings } from '../../core/settings';
import type { ModelSettings, Preset, ProviderConfig, RoleModelConfig } from '../../core/types';
import { loadOfficialModelOptions, type OfficialModelOption } from '../chat/officialModelCatalog';
import { useProviderModels } from '../chat/useProviderModels';
import { FramedBox, FramedButton, FramedHeading, FramedInput, FramedSelect, FramedSwitch, FramedText } from './Framed';
import { ROLE_MODEL_META as meta } from './roleModelLayout';

type NumberFields = Pick<ModelSettings, 'maxContext' | 'maxOutputTokens' | 'temperature' | 'topP'>;
type RawNumbers = { [K in keyof NumberFields]: string };
const numbers = (settings: NumberFields): RawNumbers => ({ maxContext: String(settings.maxContext), maxOutputTokens: String(settings.maxOutputTokens), temperature: String(settings.temperature), topP: String(settings.topP) });

interface Props { draft: Preset; onApply: (config: RoleModelConfig) => void; onCancel: () => void; }

export function RoleModelEditor({ draft, onApply, onCancel }: Props): ReactElement {
  const initial = draft.modelConfig;
  const [providerId, setProviderId] = useState(initial?.providerId ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const [raw, setRaw] = useState(() => numbers(initial?.settings ?? getDefaultModelSettings()));
  const [thinking, setThinking] = useState<ModelSettings['thinkingLevel']>(initial?.settings.thinkingLevel ?? 'off');
  const [stream, setStream] = useState(initial?.settings.stream ?? true);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [official, setOfficial] = useState<OfficialModelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectedProvider = providers.find(provider => provider.id === providerId && provider.enabled && providerPurpose(provider) === 'chat') ?? null;
  const catalog = useProviderModels(providerId === SERVER_PROVIDER_ID ? null : selectedProvider);
  const officialSelected = providerId === SERVER_PROVIDER_ID;
  const selectedOfficial = official.find(option => option.slug === model || option.label === model);

  useEffect(() => {
    let active = true;
    void Promise.allSettled([listProviders(), loadOfficialModelOptions(), listConversations()]).then(([providerResult, modelResult, conversationResult]) => {
      if (!active) return;
      if (providerResult.status === 'fulfilled') setProviders(providerResult.value);
      if (modelResult.status === 'fulfilled') setOfficial(modelResult.value);
      if (!initial && conversationResult.status === 'fulfilled') {
        const related = conversationResult.value.filter(conversation => conversation.presetId === draft.id && conversation.modelKind !== 'image').sort((a, b) => b.updatedAt - a.updatedAt);
        const availableProviders = providerResult.status === 'fulfilled' ? providerResult.value : [];
        const previous = related.find(conversation => conversation.model?.trim() && (conversation.providerId === SERVER_PROVIDER_ID
          || availableProviders.some(provider => provider.id === conversation.providerId && provider.enabled && providerPurpose(provider) === 'chat'))) ?? related[0];
        if (previous) {
          setProviderId(previous.providerId ?? ''); setModel(previous.model ?? '');
          setRaw(numbers(previous.settings)); setThinking(previous.settings.thinkingLevel); setStream(previous.settings.stream);
        }
      }
      if (providerResult.status === 'rejected' || modelResult.status === 'rejected') setNotice('部分模型列表暂未加载，请稍后重新进入或使用已配置的提供商。');
      setLoading(false);
    });
    return () => { active = false; };
  }, [draft.id, initial]);

  const chooseModel = (value: string): void => {
    setError(null); setModel(value);
    const option = officialSelected ? official.find(row => row.slug === value) : undefined;
    if (option) setRaw(current => ({ ...current, maxContext: String(option.contextTokens), maxOutputTokens: String(option.maxOutputTokens) }));
  };
  const apply = (): void => {
    setError(null);
    try {
      if (Object.values(raw).some(value => value.trim() === '')) throw new Error('请完整填写最大上下文、最大输出 token、温度和 TopP');
      const config = parseRoleModelConfig({ providerId, model, settings: {
        maxContext: Number(raw.maxContext), maxOutputTokens: Number(raw.maxOutputTokens),
        temperature: Number(raw.temperature), topP: Number(raw.topP), thinkingLevel: thinking, stream,
      } });
      resolveRoleConversation({ ...draft, modelConfig: config }, undefined, providers);
      if (officialSelected) {
        if (!selectedOfficial) throw new Error('请选择可用的栀歆文字模型；模型列表未加载时请稍后重试');
        validateRoleModelLimits(config, selectedOfficial);
      }
      onApply(config);
    } catch (reason) { setError(reason instanceof Error ? reason.message : '模型配置无效'); }
  };
  const numeric = (key: keyof RawNumbers): ReactElement => (
    <>
      <FramedText meta={meta[`${key}Label`]} className="zs-field-label">{meta[key].label}</FramedText>
      <FramedInput meta={meta[key]} className="zs-input" type="number" value={raw[key]} disabled={loading}
        onValueChange={value => { setRaw(current => ({ ...current, [key]: value })); setError(null); }} />
    </>
  );
  const inheritedModelMissing = model !== '' && officialSelected && !selectedOfficial;
  return (
    <FramedBox meta={meta.root} className="zs-page" surface="page">
      <FramedBox meta={meta.header} className="zs-header" />
      <FramedHeading meta={meta.title} className="zs-title">模型配置</FramedHeading>
      <FramedButton meta={meta.cancel} className="zs-button zs-button-ghost" onClick={onCancel}>取消</FramedButton>
      <FramedButton meta={meta.apply} className="zs-button zs-button-primary" disabled={loading} onClick={apply}>应用</FramedButton>
      {error ? <FramedText meta={meta.error} className="zs-error zs-editor-error" title={error}>{error}</FramedText> : null}
      <FramedBox meta={meta.form} className="zs-scroll"
        style={{ transform: error ? 'translateY(68px)' : undefined, height: error ? 604 : 672 }}>
        <div className="zs-canvas" style={{ height: 660 }}>
          <FramedText meta={meta.providerLabel} className="zs-field-label">模型提供商</FramedText>
          <FramedSelect meta={meta.provider} className="zs-input" value={providerId} disabled={loading}
            onValueChange={value => { setProviderId(value); setModel(''); setError(null); }}>
            <option value="">{loading ? '正在加载提供商…' : '请选择模型提供商'}</option>
            <option value={SERVER_PROVIDER_ID}>栀歆模型</option>
            {providers.filter(provider => provider.enabled && providerPurpose(provider) === 'chat').map(provider => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
            {providerId && providerId !== SERVER_PROVIDER_ID && !selectedProvider ? <option value={providerId}>原提供商不可用</option> : null}
          </FramedSelect>
          <FramedText meta={meta.modelLabel} className="zs-field-label">模型</FramedText>
          {officialSelected ? (
            <FramedSelect meta={meta.model} className="zs-input" value={selectedOfficial?.slug ?? model} disabled={loading} onValueChange={chooseModel}>
              <option value="">请选择栀歆文字模型</option>
              {inheritedModelMissing ? <option value={model}>{model}</option> : null}
              {official.map(option => <option key={option.slug} value={option.slug}>{option.label}</option>)}
            </FramedSelect>
          ) : (
            <>
              <FramedInput meta={meta.model} className="zs-input" value={model} placeholder="输入模型名称" disabled={loading} list="role-provider-models" onValueChange={chooseModel} />
              <datalist id="role-provider-models">{catalog.models.map(name => <option key={name} value={name} />)}</datalist>
            </>
          )}
          <FramedText meta={meta.limits} className="zs-model-status">
            {loading ? '正在读取现有模型配置…' : selectedOfficial ? `模型上限：上下文 ${selectedOfficial.contextTokens}，输出 ${selectedOfficial.maxOutputTokens}`
              : notice ?? (catalog.error ?? (catalog.loading ? '正在读取提供商模型…' : ''))}
          </FramedText>
          {numeric('maxContext')}
          {numeric('maxOutputTokens')}
          {numeric('temperature')}
          {numeric('topP')}
          <FramedText meta={meta.thinkingLabel} className="zs-field-label">思考等级</FramedText>
          <FramedSelect meta={meta.thinking} className="zs-input" value={thinking} disabled={loading} onValueChange={value => setThinking(value as ModelSettings['thinkingLevel'])}>
            <option value="off">关闭</option><option value="low">低</option><option value="medium">中</option><option value="high">高</option>
          </FramedSelect>
          <FramedText meta={meta.streamLabel} className="zs-field-label">流式输出</FramedText>
          <FramedSwitch meta={meta.stream} className={`zs-switch zs-media-switch${stream ? ' zs-switch-on' : ''}`} checked={stream} disabled={loading} onToggle={setStream}>
            <span className="zs-media-switch-thumb" aria-hidden="true" />
          </FramedSwitch>
        </div>
      </FramedBox>
    </FramedBox>
  );
}
